import { motion } from "framer-motion";
import { Factory, ShieldCheck, FlaskConical, TrainFront } from "lucide-react";

const TEXTURE =
  "https://images.unsplash.com/photo-1573345173719-5fbd4783d3c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwY2VtZW50JTIwYXJjaGl0ZWN0dXJhbCUyMGNvbmNyZXRlfGVufDB8fHx8MTc4ODE3MDA1Nnww&ixlib=rb-4.1.0&q=85&w=1600";

const CHAPTERS = [
  {
    no: "01",
    icon: Factory,
    title: "Factory-Direct Fresh Clinker Transit",
    body: "Zero depot storage delays. Bags move from manufacturing plant to your site in one sealed chain — freshness you can measure in setting time.",
    testid: "manifesto-chapter-1",
  },
  {
    no: "02",
    icon: ShieldCheck,
    title: "100% Tamper-Proof & Moisture Barrier Bagging",
    body: "Every 50kg bag is HDPE laminated and tamper-evident. Monsoon transit, open yards, long hauls — the cement inside stays kiln-dry.",
    testid: "manifesto-chapter-2",
  },
  {
    no: "03",
    icon: FlaskConical,
    title: "On-Site Batch Test & IS 12269 Verification",
    body: "Each consignment ships with lab certificates. Compressive strength, fineness, soundness — verified against BIS codes before you unload.",
    testid: "manifesto-chapter-3",
  },
  {
    no: "04",
    icon: TrainFront,
    title: "Pan-India Railhead & Heavy Trailer Fleet",
    body: "Dedicated rail rakes and 32ft container trailers cover every major corridor. 48-hour dispatch commitment, tracked to your gate.",
    testid: "manifesto-chapter-4",
  },
];

const CORRIDORS = ["Delhi NCR", "Mumbai", "Hyderabad", "Bengaluru", "Ahmedabad", "Kolkata", "Nagpur", "Pune", "Indore", "Lucknow", "Jaipur", "Chennai"];

export default function ManifestoSection() {
  return (
    <section id="manifesto" className="relative py-20 lg:py-28 overflow-hidden">
      <div className="absolute inset-0">
        <img src={TEXTURE} alt="" className="w-full h-full object-cover opacity-[0.07]" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/80 to-ink" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-4 flex items-center gap-3">
          <span className="h-px w-10 bg-brand inline-block" /> The Infra Edge Manifesto
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white max-w-2xl">
          Four Rules We Never Break
        </h2>

        <div className="mt-14 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.no}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              data-testid={c.testid}
              className="group bg-ink/95 p-8 lg:p-10 hover:bg-surface transition-colors"
            >
              <div className="flex items-start justify-between">
                <span className="font-mono text-5xl font-bold text-white/10 group-hover:text-brand/30 transition-colors">
                  {c.no}
                </span>
                <c.icon className="h-7 w-7 text-brand" />
              </div>
              <h3 className="font-heading text-xl sm:text-2xl font-bold uppercase text-white mt-6 leading-tight">
                {c.title}
              </h3>
              <p className="text-sm sm:text-base leading-relaxed text-gray-400 mt-4">{c.body}</p>
            </motion.div>
          ))}
        </div>

        <div id="logistics" className="mt-16">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <h3 className="font-heading text-xl sm:text-2xl font-bold uppercase text-white">
              Logistics Corridors <span className="text-brand">Live</span>
            </h3>
            <p className="font-mono text-xs uppercase tracking-widest text-gray-500">
              12 active railheads · 48hr dispatch SLA
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 border border-white/10">
            {CORRIDORS.map((city, i) => (
              <motion.div
                key={city}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-ink/95 px-4 py-5 text-center group hover:bg-surface transition-colors"
              >
                <p className="font-mono text-xs uppercase tracking-widest text-gray-300 group-hover:text-brand transition-colors">
                  {city}
                </p>
                <p className="font-mono text-[10px] text-gray-600 mt-1.5 flex items-center justify-center gap-1.5">
                  <span className="h-1 w-1 rounded-full bg-emerald-500 animate-ticker inline-block" />
                  CORRIDOR OPEN
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
